import React, { FC, useState } from 'react';
import styled from 'styled-components';

import { CheckOutlined, CloseOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';

import colors from '../../utils/colors';
import {
  ACCESS,
  CREATED_AT,
  DELETED,
  EMAIL,
  FIRST_NAME,
  ID,
  MEMBERSHIP_TYPE,
  SEARCH_BY_EMAIL,
  STATUS,
  SUBSCRIBED_USERS
} from '../../utils/constants/labels';
import PageHeader from '../common/layout/PageHeader';
import Table from '../common/table/Table';
import { api } from '../../api/api';
import { Input, Tag, Tooltip } from 'antd';
import { debounce } from '../../utils/common';
import {
  DatePreview,
  getSubdomain,
  isSubscription,
  membershipStatusColors,
  membershipTypeColors
} from '../../utils/helpers';
import { DATE_FORMAT, formatDate } from '../../utils/timeUtils';
import { BRAINTREE_DASHBOARD, STRIPE_DASHBOARD } from '../../utils/constants/urls';
import { PaymentProcessor, User } from '../../types/userTypes';
import { environment } from '../../config/configConstants';
import UserActionsModal from '../common/modal/UserActionsModal';

const columns = (openUserActionsModal: (user: User) => void) => [
  {
    title: ID,
    dataIndex: 'id',
    sorter: true,
    width: '5%',
    defaultSortOrder: 'descend'
  },
  {
    title: FIRST_NAME,
    dataIndex: 'firstName',
    width: '15%'
  },
  {
    title: EMAIL,
    dataIndex: 'email',
    sorter: true,
    width: '25%'
  },
  {
    title: MEMBERSHIP_TYPE,
    dataIndex: 'membershipType',
    width: '10%',
    render: value => <Tag color={membershipTypeColors[value]}>{value}</Tag>
  },
  {
    title: STATUS,
    dataIndex: 'membershipStatus',
    width: '5%',
    render: (value, row) =>
      isSubscription(row.membershipType) ? <Tag color={membershipStatusColors[value]}>{value}</Tag> : null
  },
  {
    title: 'Subscription/Payment',
    dataIndex: 'subscriptionOrPaymentId',
    width: '10%',
    render: (value, row) =>
      row.subscriptionId ? (
        <a
          href={
            row.paymentProcessor === PaymentProcessor.STRIPE
              ? `${STRIPE_DASHBOARD}/subscriptions/${row.subscriptionId}`
              : `${BRAINTREE_DASHBOARD}/subscriptions/${row.subscriptionId}`
          }
          target={'_blank'}
        >
          {row.subscriptionId}
        </a>
      ) : (
        <a
          href={
            row.paymentProcessor === PaymentProcessor.STRIPE
              ? `${STRIPE_DASHBOARD}/payments/${row.paymentId}`
              : `${BRAINTREE_DASHBOARD}/transactions/${row.paymentId}`
          }
          target={'_blank'}
        >
          {row.paymentId}
        </a>
      )
  },
  {
    title: CREATED_AT,
    dataIndex: 'createdAt',
    sorter: true,
    width: '10%',
    render: value => (value ? <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview> : null)
  },
  {
    title: ACCESS,
    dataIndex: 'accessUntil',
    width: '10%',
    render: value => (value ? <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview> : null)
  },
  {
    title: DELETED,
    dataIndex: 'deleted',
    width: '5%',
    render: value => (value ? <CheckOutlined /> : <CloseOutlined />)
  },
  {
    title: '',
    dataIndex: 'moreOptions',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'More options'}>
          <MoreOutlined onClick={() => openUserActionsModal(row)} />
        </Tooltip>
      )
  }
];

type Props = {};

const SubscribedUsers: FC<Props> = ({}) => {
  const [refreshTable, setRefreshTable] = useState<Function>();

  const [email, setEmail] = useState('');

  const setEmailDebounced = debounce(value => setEmail(value), 400);
  const [editUser, setEditUser] = useState<User>();

  return (
    <Container>
      <PageHeader
        title={SUBSCRIBED_USERS}
        extra={[
          <Input
            key={1}
            onChange={e => setEmailDebounced(e.target.value)}
            placeholder={SEARCH_BY_EMAIL}
            prefix={<SearchOutlined />}
          />
        ]}
      />
      <Table
        columns={columns((user: User) => setEditUser(user))}
        getData={api.getAllSubscribedUsers}
        idPropName={'id'}
        searchPropName={'email'}
        searchPropValue={email}
        bindFetchFn={setRefreshTable}
      />
      <UserActionsModal
        user={editUser ? { ...editUser, role: 'STUDENT' } : undefined}
        websiteSubdomain={getSubdomain(environment)}
        onClose={() => setEditUser(undefined)}
        onUserUpdate={refreshTable!}
      />
    </Container>
  );
};

export default SubscribedUsers;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;
